import { useState } from "react";
import { ArrowUpRight, Download, Globe, Loader2, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { downloadGenieDocumentPdf } from "@/services/genie-documents/genieDocumentPdf";
import type { GenieDocumentEntry } from "@/hooks/genie-documents/useGenieDocuments";
import type { GenieDocumentBrand } from "@/types/genie-documents/genie-document";
import { genieDocumentFormatFor } from "@/types/genie-documents/genie-document";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

interface SavedGenieDocumentCardProps {
  entry: GenieDocumentEntry;
  brand: GenieDocumentBrand;
  /** Public URL of this guide, when the Genie's page is live. */
  publicUrl: string | null;
  /** Why publishing is unavailable, in the owner's words. Null when it is. */
  publishBlockedReason: string | null;
  isPublishing: boolean;
  onEdit: () => void;
  onTogglePublished: (published: boolean) => void;
  onDelete: () => void;
}

export const SavedGenieDocumentCard = ({
  entry,
  brand,
  publicUrl,
  publishBlockedReason,
  isPublishing,
  onEdit,
  onTogglePublished,
  onDelete,
}: SavedGenieDocumentCardProps) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const { row, stored, isPublished } = entry;
  const format = genieDocumentFormatFor(stored);
  const updated = row.updated_at || row.created_at;

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      await downloadGenieDocumentPdf(
        {
          title: stored.title,
          subtitle: stored.subtitle,
          template: stored.template,
          sections: stored.sections,
        },
        brand,
      );
    } catch {
      toast.error("Couldn't export the PDF");
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="flex flex-col rounded-2xl border border-gray-200 bg-white p-5 transition-shadow hover:shadow-sm">
      <div className="flex items-start gap-3">
        <GenieIcon name={format?.genieIcon ?? "document"} size={36} />
        <div className="min-w-0 flex-1">
          <p className="truncate font-semibold text-gray-900">{stored.title}</p>
          <p className="mt-0.5 text-xs text-gray-500">
            {format?.label || "Document"}
            {" · "}
            {stored.sections.length} section{stored.sections.length === 1 ? "" : "s"}
            {updated ? ` · updated ${new Date(updated).toLocaleDateString()}` : ""}
          </p>
        </div>
        <span
          className={cn(
            "flex-shrink-0 rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide",
            isPublished ? "bg-[#EBF5FF] text-[#2563EB]" : "bg-gray-100 text-gray-500",
          )}
        >
          {isPublished ? "On your page" : "Draft"}
        </span>
      </div>

      {stored.subtitle && (
        <p className="mt-3 line-clamp-2 text-[13px] leading-snug text-gray-600">{stored.subtitle}</p>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-gray-100 pt-4">
        <Button variant="outline" size="sm" onClick={onEdit} className="gap-1.5">
          <Pencil className="h-3.5 w-3.5" />
          Open
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => void handleDownload()}
          disabled={isDownloading}
          className="gap-1.5"
        >
          {isDownloading ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Download className="h-3.5 w-3.5" />
          )}
          PDF
        </Button>

        <Button
          variant="outline"
          size="sm"
          onClick={() => onTogglePublished(!isPublished)}
          disabled={isPublishing || (!isPublished && Boolean(publishBlockedReason))}
          title={!isPublished ? publishBlockedReason ?? undefined : undefined}
          className={cn("gap-1.5", isPublished && "text-gray-600")}
        >
          {isPublishing ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Globe className="h-3.5 w-3.5" />
          )}
          {isPublished ? "Unpublish" : "Publish to page"}
        </Button>

        {isPublished && publicUrl && (
          <a
            href={publicUrl}
            target="_blank"
            rel="noreferrer noopener"
            className="inline-flex items-center gap-1 text-xs font-semibold text-genie-primary hover:underline"
          >
            View
            <ArrowUpRight className="h-3 w-3" />
          </a>
        )}

        <Button
          variant="ghost"
          size="icon"
          onClick={onDelete}
          aria-label={`Delete ${stored.title}`}
          className="ml-auto h-8 w-8 text-gray-400 hover:bg-red-50 hover:text-red-600"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>

      {!isPublished && publishBlockedReason && (
        <p className="mt-3 text-[11px] leading-snug text-gray-500">{publishBlockedReason}</p>
      )}
    </div>
  );
};

export default SavedGenieDocumentCard;
